import React, { } from 'react'
import { useParams } from 'react-router-dom'
import Wrapper from '../helpers/Wrapper'
import { Helmet } from 'react-helmet'
import { apiClient } from '../helpers/Axios'
import { useQuery } from '@tanstack/react-query'
import LoadingComponent from '../components/LoadingComponent'
import { displayImage } from '../helpers/config'
import FormatCurrency from '../helpers/FormatCurrency'
import Accordion from '../components/Accordion/Accordion'

function OrderTrackingPage() {

    const { id } = useParams()

    const { data, isPending, status } = useQuery({
        queryKey: [id],
        queryFn: async () => {
            const res = await apiClient(`/track/${id}`)
            return res.data;
        },
        refetchInterval: 10000
    })


    const steps = [
        {
            title: "Placing Order",
            description: "Your order is being placed.",
        },
        {
            title: "Preparing Order",
            description: "We are preparing your order.",
        },
        {
            title: "Order Ready",
            description: "Your order is ready for delivery.",
        },
        {
            title: "Delivering",
            description: "A rider has your order.",
        },
        {
            title: "Order Completed",
            description: "Your order has been delivered.",
        },
    ];


    if (isPending) {
        return (
            <LoadingComponent />
        )
    }

    if (status === 'error') {
        return (
            <div className='text-red-500 text-center py-10'>Something Happened!</div>
        )
    }

    return (
        <div className='w-full relative mt-10 font-serif'>
            <Helmet>
                <title>Order #{id}</title>
            </Helmet>

            <Wrapper>
                <div className='w-full mx-auto max-w-6xl'>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                        {/* order Status */}
                        <div className="md:col-span-1">
                            <div className="border p-4 rounded-3xl md:p-6">
                                {steps?.map((step, i) => (
                                    <div className="flex items-start gap-7">
                                        {/* Step Indicator */}
                                        <div className="flex flex-col items-center">
                                            <div className={`size-7 rounded-full ${data?.status >= i + 1 ? "bg-green-500" : "bg-gray-100"}`} />
                                            {i !== steps.length - 1 && (
                                                <div className={`h-14 border-l-2 border-dashed ${data?.status >= i + 2 ? "border-green-500" : "border-gray-100"}`} />
                                            )}
                                        </div>

                                        <div className='-space-y-1 -mt-1'>
                                            <h1 className='font-serif font-bold text-lg'>{step?.title}</h1>
                                            <p className="text-gray-500 font-serif text-sm">{step?.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>



                        {/* Order Details */}
                        <div className="md:col-span-2">
                            <div className="border p-4 rounded-3xl md:p-6">
                                <h2 className="font-medium text-lg">Order Details</h2>

                                {/* Order Date and details */}
                                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
                                    {/* Date */}
                                    <div className='space-y-1'>
                                        <h3 className='text-sm font-medium'>Date</h3>
                                        <p className="text-gray-500 text-sm">
                                            {new Date(data?.order?.iat).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'medium' })}
                                        </p>
                                    </div>

                                    {/* Customer NAme */}
                                    <div className='space-y-1'>
                                        <h3 className='text-sm font-medium'>Customer Name</h3>
                                        <p className="text-gray-500 text-sm">{data?.order?.User?.firstname} {data?.order?.User?.lastname}</p>
                                    </div>

                                    {/* Payment Status */}
                                    <div className='space-y-1'>
                                        <h3 className='text-sm font-medium'>Payment Status</h3>
                                        <p className="text-gray-500 text-sm capitalize">{data?.order?.payment_status}</p>
                                    </div>

                                    {/* Address */}
                                    <div className='space-y-1'>
                                        <h3 className='text-sm font-medium'>Address</h3>
                                        {data?.order?.delivery_type === 'ship' ? (
                                            <div>
                                                <p className="text-gray-500 text-sm capitalize">{data?.order?.shippindAddress?.country}</p>
                                                <p className="text-gray-500 text-sm capitalize">{data?.order?.shippindAddress?.city}</p>
                                                <p className="text-gray-500 text-sm capitalize">{data?.order?.shippindAddress?.phone}</p>
                                                <p className="text-gray-500 text-sm capitalize">{data?.order?.shippindAddress?.address}</p>
                                            </div>
                                        ) : (
                                            <p className="text-red-500 text-sm capitalize">
                                                Pickup
                                            </p>
                                        )}
                                    </div>
                                </div>


                                {/* Hearder Line = */}
                                <hr className='my-5 border-gray-400' />

                                {/* Product Details */}
                                <div className="">
                                    {/* Products */}
                                    <div className="mt-3">
                                        <Accordion title="Packages" >
                                            <table className='w-full mt-4'>
                                                <tbody>
                                                    {data?.order?.order_items?.map((item, i) => (
                                                        <tr key={i}>
                                                            <td className='py-2'>
                                                                <div className="flex gap-2 items-center">
                                                                    <img className='size-16 object-cover shadow'
                                                                        src={displayImage(JSON.parse(item?.Product?.images)[0])} alt=""
                                                                    />
                                                                    <div className='flex gap-1.5 items-center'>
                                                                        <div>
                                                                            <p>{item?.Product?.name}</p>
                                                                            <p className="text-gray-500">
                                                                                {item?.variant?.color} {item?.variant?.size} {item?.variant?.material} {item?.variant?.type}
                                                                            </p>
                                                                        </div>
                                                                        <small className='text-sm'>x {item?.quantity}</small>
                                                                        <div>{item?.customisation && (
                                                                            <p>Customise: {item?.customisation}</p>
                                                                        )}</div>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className='py-2 text-right'>
                                                                <FormatCurrency amount={item?.price} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Accordion>
                                    </div>
                                </div>


                                <hr className='my-5' />

                                {/* Total Information */}


                                <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                                    {/* discount */}
                                    <div className='space-y-1'>
                                        <h3 className='font-medium'>Discount</h3>
                                        <p className="text-gray-500 text-sm capitalize">
                                            <FormatCurrency amount={data?.order?.discount} />
                                        </p>
                                    </div>
                                    {/* Shipping */}
                                    <div className='space-y-1'>
                                        <h3 className='font-medium'>Shipping</h3>
                                        <p className="text-gray-500 text-sm capitalize">
                                            <FormatCurrency amount={data?.order?.shipping} />
                                        </p>
                                    </div>
                                    {/* subtotal */}
                                    <div className='space-y-1'>
                                        <h3 className='font-semibold text-right text-red-500'>TOTAL</h3>
                                        <p className="text-gray-500 text-sm capitalize text-right">
                                            <FormatCurrency amount={data?.order?.total} />
                                        </p>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </Wrapper>
        </div>
    )
}

export default OrderTrackingPage